/**
 * TY SHOP — Predictive Search
 * Uses Shopify's /search/suggest.json API for live autocomplete.
 * Works on both desktop and mobile search inputs.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var MIN_CHARS = 2;
  var LIMIT = 4;

  document.querySelectorAll('[data-predictive-search]').forEach(function (container) {
    var input = container.querySelector('[data-predictive-input]');
    var results = container.querySelector('[data-predictive-results]');
    if (!input || !results) return;

    var debounceTimer = null;
    var activeIndex = -1;
    var currentQuery = '';
    var abortController = null;

    /* Labels from data attributes for i18n */
    var labels = {
      products: container.dataset.labelProducts || 'Prodotti',
      collections: container.dataset.labelCollections || 'Collezioni',
      pages: container.dataset.labelPages || 'Pagine',
      viewAll: container.dataset.labelViewAll || 'Vedi tutti i risultati',
      noResults: container.dataset.labelNoResults || 'Nessun risultato'
    };

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocomplete', 'off');

    results.setAttribute('role', 'listbox');
    results.id = 'predictive-results-' + Math.random().toString(36).substr(2, 6);
    input.setAttribute('aria-controls', results.id);

    /* ---- Input handler ---- */
    input.addEventListener('input', function () {
      var q = this.value.trim();
      clearTimeout(debounceTimer);
      if (q.length < MIN_CHARS) { hide(); return; }
      debounceTimer = setTimeout(function () { fetchResults(q); }, DEBOUNCE_MS);
    });

    /* ---- Keyboard navigation ---- */
    input.addEventListener('keydown', function (e) {
      var items = results.querySelectorAll('[data-predictive-item]');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        updateActive(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, -1);
        updateActive(items);
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        var link = items[activeIndex].closest('a') || items[activeIndex].querySelector('a');
        if (link) link.click();
      } else if (e.key === 'Escape') {
        hide();
        input.blur();
      }
    });

    /* ---- Focus handlers ---- */
    input.addEventListener('focus', function () {
      if (results.innerHTML.trim() && this.value.trim().length >= MIN_CHARS) {
        show();
      }
    });

    /* ---- Close on outside click ---- */
    document.addEventListener('click', function (e) {
      if (!container.contains(e.target)) hide();
    });

    /* ---- Fetch results ---- */
    function fetchResults(q) {
      if (abortController) abortController.abort();
      abortController = new AbortController();
      currentQuery = q;

      var url = '/search/suggest.json?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection,page'
        + '&resources[limit]=' + LIMIT;

      fetch(url, { signal: abortController.signal })
        .then(function (resp) { return resp.json(); })
        .then(function (data) {
          if (q !== currentQuery) return;
          var res = data.resources.results;
          render(res, q);

          /* `/search/suggest.json` non matcha lo SKU variante (es. "AID12959"),
             solo la pagina di ricerca completa `/search` lo fa (stesso indice,
             soglie di rilevanza diverse — limite noto di Shopify, non del tema).
             Se non ci sono prodotti, proviamo un fallback sulla ricerca vera. */
          if (!res.products || !res.products.length) {
            fetchSkuFallback(q, res);
          }
        })
        .catch(function (e) {
          if (e.name !== 'AbortError') hide();
        });
    }

    /* ---- Fallback: cerca per SKU sulla pagina di ricerca completa ---- */
    function fetchSkuFallback(q, suggestRes) {
      var fallbackController = new AbortController();
      abortController = fallbackController;

      fetch('/search?q=' + encodeURIComponent(q) + '&type=product', { signal: fallbackController.signal })
        .then(function (resp) { return resp.text(); })
        .then(function (html) {
          if (q !== currentQuery) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = Array.prototype.slice.call(doc.querySelectorAll('.product-card')).slice(0, LIMIT);
          if (!cards.length) return;

          var products = cards.map(function (card) {
            var linkEl = card.querySelector('.product-card__title a') || card.querySelector('.product-card__link');
            var imgEl = card.querySelector('.product-card__image--primary');
            var priceEl = card.querySelector('.product-card__price-wrap');
            var priceText = '';
            if (priceEl) {
              /* price-wrap incorpora un <style> scoped (snippets/price.liquid):
                 va rimosso prima di leggere il testo, altrimenti il CSS finisce nel prezzo. */
              var priceClone = priceEl.cloneNode(true);
              Array.prototype.forEach.call(priceClone.querySelectorAll('style'), function (s) { s.remove(); });
              priceText = priceClone.textContent.replace(/\s+/g, ' ').trim();
            }
            return {
              url: linkEl ? linkEl.getAttribute('href') : '#',
              title: linkEl ? linkEl.textContent.trim() : '',
              imgSrc: imgEl ? (imgEl.getAttribute('src') || '') : '',
              priceText: priceText
            };
          });

          render(suggestRes, q, products);
        })
        .catch(function (e) { /* silenzioso: la UI resta su quanto gia' mostrato da suggest.json */ });
    }

    /* ---- Render results ---- */
    function render(res, q, fallbackProducts) {
      var html = '';
      var hasResults = false;

      /* Products — fallback SKU (fetchSkuFallback) ha priorita' se presente:
         arriva solo quando suggest.json non ha trovato nulla, in un secondo
         giro dopo il render iniziale. */
      if (fallbackProducts && fallbackProducts.length) {
        hasResults = true;
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title">' + labels.products + '</p>';
        fallbackProducts.forEach(function (p) {
          var img = p.imgSrc ? '<img class="predictive-search__item-img" src="' + p.imgSrc + '" alt="" width="48" height="48" loading="lazy">' : '';
          html += '<a href="' + p.url + '" class="predictive-search__item" data-predictive-item role="option">'
            + img
            + '<div class="predictive-search__item-info">'
            + '<span class="predictive-search__item-title">' + escapeHtml(p.title) + '</span>'
            + '<span class="predictive-search__item-price">' + escapeHtml(p.priceText) + '</span>'
            + '</div></a>';
        });
        html += '</div>';
      } else if (res.products && res.products.length) {
        hasResults = true;
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title">' + labels.products + '</p>';
        res.products.forEach(function (p) {
          var imgSrc = p.image ? p.image.replace(/(\.[a-z]+)(\?|$)/, '_100x$1$2') : '';
          var img = imgSrc ? '<img class="predictive-search__item-img" src="' + imgSrc + '" alt="" width="48" height="48" loading="lazy">' : '';
          /* suggest.json restituisce il prezzo in euro (stringa, es. "9.45");
             formatMoney vuole i centesimi → convertiamo prima di formattare. */
          var priceCents = Math.round(parseFloat(p.price) * 100);
          var price = window.TYShop && window.TYShop.formatMoney
            ? window.TYShop.formatMoney(priceCents)
            : ('€' + parseFloat(p.price).toFixed(2).replace('.', ','));
          /* p.price e' il prezzo MINIMO del prodotto: se le varianti costano
             diverso va prefissato con "Da", come sulle card catalogo, altrimenti
             sembra il prezzo pieno. suggest.json espone price_min/price_max ma
             NON l'elenco varianti (variants: []), quindi qui non si puo' risalire
             al compare_at_price della variante piu' economica: la ricerca rapida
             infatti non mostra ne' barrato ne' badge sconto. */
          var pMin = parseFloat(p.price_min);
          var pMax = parseFloat(p.price_max);
          var priceVaries = !isNaN(pMin) && !isNaN(pMax) && pMin !== pMax;
          var priceHtml = (priceVaries ? '<span class="predictive-search__item-from">Da</span>&nbsp;' : '') + price;
          html += '<a href="' + p.url + '" class="predictive-search__item" data-predictive-item role="option">'
            + img
            + '<div class="predictive-search__item-info">'
            + '<span class="predictive-search__item-title">' + escapeHtml(p.title) + '</span>'
            + '<span class="predictive-search__item-price">' + priceHtml + '</span>'
            + '</div></a>';
        });
        html += '</div>';
      }

      /* Collections */
      if (res.collections && res.collections.length) {
        hasResults = true;
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title">' + labels.collections + '</p>';
        res.collections.forEach(function (c) {
          html += '<a href="' + c.url + '" class="predictive-search__item predictive-search__item--text" data-predictive-item role="option">'
            + '<span>' + escapeHtml(c.title) + '</span></a>';
        });
        html += '</div>';
      }

      /* Pages */
      if (res.pages && res.pages.length) {
        hasResults = true;
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title">' + labels.pages + '</p>';
        res.pages.forEach(function (pg) {
          html += '<a href="' + pg.url + '" class="predictive-search__item predictive-search__item--text" data-predictive-item role="option">'
            + '<span>' + escapeHtml(pg.title) + '</span></a>';
        });
        html += '</div>';
      }

      if (!hasResults) {
        html = '<p class="predictive-search__no-results">' + labels.noResults + '</p>';
      }

      /* View all link */
      html += '<a href="/search?q=' + encodeURIComponent(q) + '" class="predictive-search__view-all" data-predictive-item role="option">'
        + labels.viewAll + ' &rarr;</a>';

      results.innerHTML = html;
      activeIndex = -1;
      show();
    }

    /* ---- Helpers ---- */
    function show() {
      results.style.display = '';
      input.setAttribute('aria-expanded', 'true');
    }

    function hide() {
      results.style.display = 'none';
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    }

    function updateActive(items) {
      items.forEach(function (item, i) {
        if (i === activeIndex) {
          item.classList.add('predictive-search__item--active');
          item.setAttribute('aria-selected', 'true');
          input.setAttribute('aria-activedescendant', item.id || '');
        } else {
          item.classList.remove('predictive-search__item--active');
          item.removeAttribute('aria-selected');
        }
      });
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }
  });
})();
